/**
 * Strike 3 — Demo Mode banner. Renders only when the logged-in user is an admin
 * AND demo mode is currently active on the server. Shows a live countdown.
 * Mounted globally in App.js.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function formatRemaining(secs) {
  if (!secs || secs <= 0) return "expired";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function DemoModeBanner() {
  const { user } = useAuth();
  const [state, setState] = useState({ active: false, seconds_remaining: 0 });
  const [dismissed, setDismissed] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const fetchState = async () => {
      try {
        const r = await axios.get(`${API}/api/admin/demo-mode`, { withCredentials: true });
        if (!cancelled) setState(r.data);
      } catch { /* silent */ }
    };
    fetchState();
    const id = setInterval(fetchState, 60000); // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  // Live countdown — decrement locally between server refreshes
  useEffect(() => {
    if (!state.active) return undefined;
    const id = setInterval(() => {
      setState(prev => ({ ...prev, seconds_remaining: Math.max(0, (prev.seconds_remaining || 0) - 1) }));
    }, 1000);
    return () => clearInterval(id);
  }, [state.active]);

  if (!isAdmin || !state.active || dismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-400 text-navy border-b-2 border-amber-600 shadow-lg" data-testid="demo-mode-banner">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="font-dm font-bold text-sm">DEMO MODE ACTIVE</span>
          <span className="font-dm text-xs hidden sm:inline">— Intelligence writes paused. Banner visible to admins only.</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="font-dm text-xs font-semibold whitespace-nowrap">Expires in {formatRemaining(state.seconds_remaining)}</span>
          <button onClick={() => setDismissed(true)} className="hover:bg-amber-500 rounded p-1" data-testid="demo-banner-dismiss">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
